import { useState, useEffect, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ModalKey, ModalContext } from 'components/Modals/ModalContext'
import { useGetGroups } from 'redux/customHooks/useGetGroups'
import { useGetCoaches } from 'redux/customHooks/useGetCoaches'

import ScreenFrame from 'components/Frame/ScreenFrame'
import SearchBar from 'components/Search/SearchBar'
import BrandColorButton from 'components/Buttons/BrandColorButton'
import PaginationController from 'components/Pagination/PaginationController'
import ContentFrame from 'components/Frame/ContentFrame'
import GroupListItem from 'components/Group/GroupListItem'

export default function GroupList() {
    const navigate = useNavigate()

    const { ShowModal } = useContext(ModalContext)
    
    const groups = useGetGroups()
    const coaches = useGetCoaches()

    const requirePaymentMethodToInviteAthletes = useSelector((state) => state.user.requirePaymentMethodToInviteAthletes)
    const organizationAdminStripeCustomerId = useSelector((state) => state.user.organizationAdminStripeCustomerId)

    const [searchTerm, setSearchTerm] = useState('')
    const [showFilters, setShowFilters] = useState(false)
    const [filteredGroups, setFilteredGroups] = useState([])
    const [paginatedGroups, setPaginatedGroups] = useState([])
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 10,
        totalCount: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false
    })

    const OnCreateGroup = () => {
        if (HasOrganizationPaymentMethodBeenAdded() === false) {
            ShowPaymentMethodRequiredWarning()
            return
        }

        navigate('/groups/create')
    }

    const HasOrganizationPaymentMethodBeenAdded = () => {
        if (requirePaymentMethodToInviteAthletes === 0) { 
            return true
        }

        if (requirePaymentMethodToInviteAthletes === 1) {

            if (organizationAdminStripeCustomerId === '') {
                return false
            }

            return true
        }
    }

    const ShowPaymentMethodRequiredWarning = () => {
        const modalData = {
            key: ModalKey.ActionWarning,
            size: 'quarter',
            titleText: 'Unable to add athletes',
            bodyText: [
                'In order to add additional athletes a payment method must be added to the organization.',
                'A payment method can be added by the organization owner'
            ],
            buttonText: 'Add payment method',
            buttonAction: () => navigate('/settings/billing')
        }

        ShowModal(modalData)
    }

    const FilterGroupsBySearchTerm = (group) => {
        return group.name.toLowerCase().includes(searchTerm.toLowerCase())
    }

    const HandlePageSizeChange = (newPageSize) => {
        setPagination(prev => ({
            ...prev,
            pageSize: parseInt(newPageSize),
            page: 1
        }))
    }

    // Update filtered athletes when search term or coach filter changes
    useEffect(() => {
        if (groups !== null) {
            const filtered = groups.filter(item => 
                FilterGroupsBySearchTerm(item) 
            )

            setFilteredGroups(filtered)
        }
    }, [groups, searchTerm])

    // Update pagination and paginated athletes when filteredGroups or pagination values change
    useEffect(() => {
        if (filteredGroups.length > 0) {
            const totalCount = filteredGroups.length
            const totalPages = Math.ceil(totalCount / pagination.pageSize) || 1
            const currentPage = Math.min(pagination.page, totalPages || 1)

            // Update pagination
            setPagination(prev => ({
                ...prev,
                totalCount,
                totalPages,
                page: currentPage,
                hasNext: currentPage < totalPages,
                hasPrevious: currentPage > 1
            }))

            // Update paginated athletes
            const startIndex = (currentPage - 1) * pagination.pageSize
            const endIndex = startIndex + pagination.pageSize

            setPaginatedGroups(filteredGroups.slice(startIndex, endIndex))

        } else {
            setPagination(prev => ({
                ...prev,
                totalCount: 0,
                totalPages: 0,
                page: 1,
                hasNext: false,
                hasPrevious: false
            }))

            setPaginatedGroups([])
        }
    }, [filteredGroups, pagination.page, pagination.pageSize])

    if (groups === null || coaches === null) { 
        return (
            <ScreenFrame>
                <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                <div className='w-full h-[800px] bg-gray-200 rounded-lg animate-pulse' />
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame>
            <div className='flex items-center gap-x-4'>
                <h1 className="text-2xl font-semibold text-gray-900 tracking-tight pt-2 flex-grow">
                    Groups
                </h1>

                <div className="w-40">
                    <BrandColorButton 
                        text="Create Group" 
                        action={OnCreateGroup}
                        className='shadow-md'
                    />
                </div> 
            </div>

            <SearchBar 
                placeholder="Enter Group Name"
                searchTerm={searchTerm} 
                setSearchTerm={setSearchTerm} 
                showFilters={showFilters} 
                setShowFilters={setShowFilters} 
            />

            {/* Filters */}
            <div>
                <div
                    className='-mb-4 flex gap-4 transition-all duration-200 select-none'
                    style={{
                        maxHeight: showFilters ? '100px' : '0px',
                        opacity: showFilters ? 1 : 0,
                        paddingBottom: showFilters ? '1rem' : '0',
                        pointerEvents: showFilters ? 'auto' : 'none'
                    }}
                >
                    {/* Items per page */}
                    <div className='flex items-center gap-x-1'>
                        <p className='text-sm text-gray-700 '>Items per page:</p>

                        <div className="border border-gray-200 rounded-md shadow-md">
                            <select
                                value={pagination.pageSize}
                                className="px-2 py-1 text-sm border-r-[10px] border-white rounded-md cursor-pointer focus:outline-none"
                                onChange={(e) => HandlePageSizeChange(e.target.value)}
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <PaginationController pagination={pagination} setPagination={setPagination} />

            <ContentFrame className='divide-y divide-gray-200'>
                {paginatedGroups.length === 0 &&
                    <div className="text-center text-gray-500 py-4">No groups found</div>
                }

                {paginatedGroups.length > 0 && paginatedGroups.map((group, index) => (
                    <GroupListItem key={index} group={group} coaches={coaches} />
                ))}
            </ContentFrame>
        
            <PaginationController pagination={pagination} setPagination={setPagination} />
        </ScreenFrame>
    )
}
